import { useRef, useEffect } from 'react'
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import PricingCTA from './PricingCTA';
import { Link } from 'react-router-dom';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Movement = () => {
  const heroVideoRef = useRef(null);
  const demoVideoRef = useRef(null);

  useEffect(() => {
    fetch('/videos/movement.mp4')
      .then(response => {
        if (!response.ok) {
          console.error('Video file not found or not accessible');
        }
      })
      .catch(error => console.error('Error checking video:', error));
  }, []);

  // Updated chart data to show weekday patterns
  const chartData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Pedestrian Traffic',
        data: [2150, 2300, 2400, 2300, 2800, 1900, 1200],
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Vehicle Traffic',
        data: [3200, 3400, 3300, 3400, 3900, 2500, 1800],
        borderColor: 'rgba(53, 162, 235, 1)',
        backgroundColor: 'rgba(53, 162, 235, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
        },
      },
      x: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
        },
      },
    },
    plugins: {
      legend: {
        labels: {
          color: 'rgba(255, 255, 255, 0.9)',
          font: {
            family: "'Inter', sans-serif",
            weight: 300,
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: {
          family: "'Inter', sans-serif",
          weight: 300,
        },
        bodyFont: {
          family: "'Inter', sans-serif",
          weight: 300,
        },
      },
    },
  };

  return (
    <div className="absolute inset-0 w-full bg-black overflow-y-auto">
      {/* Hero Section */}
      <div className="h-screen relative">
        <div className="absolute inset-0 bg-black/50 z-10" />
        <video
          ref={heroVideoRef}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => console.error('Video error:', e)}
        >
          <source
            src="/videos/movement.mp4"
            type="video/mp4"
          />
        </video>

        <div className="relative z-20 container mx-auto px-4 pt-32">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl text-white font-light tracking-wider mb-6">
              Prove Your Retail Listing's Value
            </h1>
            <p className="text-xl md:text-2xl text-white/80 font-light tracking-wide mb-8">
              Real foot and vehicle traffic counts for San Francisco retail listings. Show prospects exactly how busy the location is.
            </p>
            <Link
              to="/pricing"
              className="inline-block bg-white hover:bg-white/90 text-black px-8 py-4 rounded-lg text-lg transition-colors font-medium"
            >
              Get Your Traffic Report
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 text-white/60 animate-bounce">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>

      {/* Main Content Section */}
      <div className="relative bg-black/95 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl text-white font-light tracking-wider mb-6">
              Data That Closes Retail Listings
            </h2>
            <p className="text-lg text-white/80 font-light tracking-wide mb-12 max-w-3xl">
              Stop guessing about traffic. Get precise foot and vehicle counts for any retail listing in SF.
            </p>

            {/* Demo Video Container */}
            <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-900 mb-16 shadow-2xl">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/prOWJkW_xXk"
                title="Movement Demo Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>

            {/* Traffic Analysis Section */}
            <div className="mt-24 mb-24">
              <h2 className="text-3xl md:text-4xl text-white font-light tracking-wider mb-6">
                Give Retailers The Full Picture
              </h2>
              <p className="text-lg text-white/80 font-light tracking-wide mb-12 max-w-3xl">
                Provide detailed traffic patterns that help retailers understand your location's potential. From peak hours to weekly trends, give them the data they need to say yes.
              </p>

              {/* Chart Container */}
              <div className="bg-white/5 rounded-lg p-6 mb-16">
                <div className="h-[400px]">
                  <Line data={chartData} options={chartOptions} />
                </div>
              </div>

              {/* Key Metrics Grid */}
              <div className="grid md:grid-cols-4 gap-6">
                <div className="bg-white/5 rounded-lg p-6 text-center">
                  <h3 className="text-3xl text-white font-light mb-2">15,450</h3>
                  <p className="text-white/80">Weekly Pedestrians</p>
                </div>
                <div className="bg-white/5 rounded-lg p-6 text-center">
                  <h3 className="text-3xl text-white font-light mb-2">21,500</h3>
                  <p className="text-white/80">Weekly Vehicles</p>
                </div>
                <div className="bg-white/5 rounded-lg p-6 text-center">
                  <h3 className="text-3xl text-white font-light mb-2">2,800</h3>
                  <p className="text-white/80">Lunchtime Peak</p>
                </div>
                <div className="bg-white/5 rounded-lg p-6 text-center">
                  <h3 className="text-3xl text-white font-light mb-2">12-2pm</h3>
                  <p className="text-white/80">Busiest Hours</p>
                </div>
              </div>
            </div>

            {/* Features Section */}
            <div className="mt-24 mb-24">
              <h2 className="text-3xl md:text-4xl text-white font-light tracking-wider mb-12">
                Everything Retailers Need to Know
              </h2>

              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white/5 rounded-lg p-8">
                  <h3 className="text-xl text-white font-light tracking-wide mb-4">
                    Traffic Patterns
                  </h3>
                  <ul className="space-y-4">
                    <li className="flex items-start text-white/80">
                      <svg className="w-5 h-5 mr-3 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Hourly foot traffic counts</span>
                    </li>
                    <li className="flex items-start text-white/80">
                      <svg className="w-5 h-5 mr-3 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Vehicle traffic patterns</span>
                    </li>
                    <li className="flex items-start text-white/80">
                      <svg className="w-5 h-5 mr-3 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Peak hours and slow periods</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white/5 rounded-lg p-8">
                  <h3 className="text-xl text-white font-light tracking-wide mb-4">
                    Location Insights
                  </h3>
                  <ul className="space-y-4">
                    <li className="flex items-start text-white/80">
                      <svg className="w-5 h-5 mr-3 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Weekday vs weekend patterns</span>
                    </li>
                    <li className="flex items-start text-white/80">
                      <svg className="w-5 h-5 mr-3 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Monthly trend analysis</span>
                    </li>
                    <li className="flex items-start text-white/80">
                      <svg className="w-5 h-5 mr-3 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Seasonal variations</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white/5 rounded-lg p-8">
                  <h3 className="text-xl text-white font-light tracking-wide mb-4">
                    Easy Sharing
                  </h3>
                  <ul className="space-y-4">
                    <li className="flex items-start text-white/80">
                      <svg className="w-5 h-5 mr-3 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Branded PDF reports</span>
                    </li>
                    <li className="flex items-start text-white/80">
                      <svg className="w-5 h-5 mr-3 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Direct data access for tenants</span>
                    </li>
                    <li className="flex items-start text-white/80">
                      <svg className="w-5 h-5 mr-3 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Mobile-friendly dashboard</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-white/5 rounded-lg p-12 text-center">
              <h2 className="text-3xl md:text-4xl text-white font-light tracking-wider mb-6">
                Help Retailers See Your Listing's Potential
              </h2>
              <p className="text-xl text-white/80 font-light tracking-wide mb-8 max-w-2xl mx-auto">
                Get precise foot and vehicle traffic counts for your retail listings.
              </p>
              <Link
                to="/pricing"
                className="inline-block bg-white hover:bg-white/90 text-black px-8 py-4 rounded-lg text-lg transition-colors font-medium"
              >
                Track Your Listing's Traffic
              </Link>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Movement; 